import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import l from '../../../../lang';
import { openNewTabAndFocus } from '../../../../utils';
import { Tooltip } from '../../../common/tooltip';
import ArrowRightIcon from '../../../../assets/icons/arrow-right-blue.svg?url';
import ClockIcon from '../../../../assets/icons/api-solutions/clock.svg?url';
import UsersIcon from '../../../../assets/icons/api-solutions/users.svg?url';

export type SolutionId =
  | 'checkoutRedirect'
  | 'subscriptions'
  | 'recurringPayment'
  | 'splitPayments'
  | 'oneClickUpsell'
  | 'transparentCheckout';

export type SolutionDifficulty = 'low' | 'medium' | 'high';

export type SolutionConfig = {
  id: SolutionId;
  icon: string;
  docsUrl: string;
  difficulty: SolutionDifficulty;
};

const DIFFICULTY_LEVEL: Record<SolutionDifficulty, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

type ApiSolutionItemProps = {
  solution: SolutionConfig;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
};

const DifficultyMeter = ({ level }: { level: SolutionDifficulty }) => {
  const filled = DIFFICULTY_LEVEL[level];

  return (
    <span className={`api_solution_item__difficulty api_solution_item__difficulty--${level}`} aria-hidden>
      {[1, 2, 3, 4].map(bar => (
        <span
          key={bar}
          className={`api_solution_item__difficulty-bar${bar <= filled ? ' api_solution_item__difficulty-bar--filled' : ''}`}
        />
      ))}
    </span>
  );
};

const ApiSolutionItem = ({ solution, expanded, onChange }: ApiSolutionItemProps) => {
  const titleKey = `paymentIntegration.api.page.solutions.${solution.id}.title`;
  const idealKey = `paymentIntegration.api.page.solutions.${solution.id}.ideal`;
  const descriptionKey = `paymentIntegration.api.page.solutions.${solution.id}.description`;
  const audienceKey = `paymentIntegration.api.page.solutions.${solution.id}.audience`;
  const timeKey = `paymentIntegration.api.page.solutions.${solution.id}.time`;

  return (
    <Accordion
      className="api_solution_item"
      expanded={expanded}
      onChange={(_, nextExpanded) => onChange(nextExpanded)}
      disableGutters
      elevation={0}
    >
      <AccordionSummary
        className="api_solution_item__summary"
        expandIcon={<ExpandMoreIcon className="api_solution_item__chevron" />}
      >
        <div className="api_solution_item__header">
          <span className="api_solution_item__icon">
            <img src={solution.icon} alt="" aria-hidden />
          </span>
          <div className="api_solution_item__texts">
            <div className="api_solution_item__title">{l(titleKey)}</div>
            <div className="api_solution_item__ideal api_solution_item__ideal--mobile">
              {l('paymentIntegration.api.page.solutions.idealFor')} {l(idealKey)}
            </div>
          </div>
          <div className="api_solution_item__ideal api_solution_item__ideal--desktop">
            {l('paymentIntegration.api.page.solutions.idealFor')} {l(idealKey)}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className="api_solution_item__details">
        <p className="api_solution_item__description">{l(descriptionKey)}</p>
          <div className="api_solution_item__meta">
            <div className="api_solution_item__meta-row">
              <img src={UsersIcon} alt="" aria-hidden className="api_solution_item__meta-icon" />
              <span className="api_solution_item__meta-text">{l(audienceKey)}</span>
            </div>
            <div className="api_solution_item__meta-row">
              <Tooltip
                title={l('paymentIntegration.api.page.solutions.timeTooltip')}
                arrow
                placement="bottom"
                classes={{
                  tooltip: 'api_solution_item__tooltip',
                  arrow: 'api_solution_item__tooltip-arrow',
                }}
              >
                <span className="api_solution_item__metric">
                  <img src={ClockIcon} alt="" aria-hidden className="api_solution_item__meta-icon" />
                  <span className="api_solution_item__meta-text">{l(timeKey)}</span>
                </span>
              </Tooltip>
              <Tooltip
                title={l('paymentIntegration.api.page.solutions.levelTooltip')}
                arrow
                placement="bottom"
                classes={{
                  tooltip: 'api_solution_item__tooltip',
                  arrow: 'api_solution_item__tooltip-arrow',
                }}
              >
                <span className="api_solution_item__metric api_solution_item__metric--difficulty">
                  <DifficultyMeter level={solution.difficulty} />
                </span>
              </Tooltip>
            </div>
          </div>
        <button
          type="button"
          className="api_solution_item__docs"
          onClick={event => {
            event.stopPropagation();
            openNewTabAndFocus(solution.docsUrl);
          }}
        >
          {l('paymentIntegration.api.page.solutions.cta')}
          <img src={ArrowRightIcon} alt="" aria-hidden />
        </button>
      </AccordionDetails>
    </Accordion>
  );
};

export default ApiSolutionItem;
